import type { ConnectionDiagnostics } from '../../contexts/PlatformEventContext';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip';

interface ConnectionIndicatorProps {
  isConnected: boolean;
  diagnostics?: ConnectionDiagnostics;
  className?: string;
}

function formatUptime(since: number | null): string {
  if (!since) return '—';
  const seconds = Math.round((Date.now() - since) / 1000);
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}

function formatTimestamp(ts: number | null): string {
  if (!ts) return '—';
  return new Date(ts).toLocaleTimeString();
}

/**
 * Status dot showing the current WebSocket connection state. Used on
 * pages and cards that stream live events from the platform event bus.
 * Hover to see status + diagnostics.
 */
export function ConnectionIndicator({ isConnected, diagnostics, className = '' }: ConnectionIndicatorProps) {
  const status = isConnected ? 'Connected' : 'Disconnected';
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={`inline-flex items-center ${className}`}
            data-testid="connection-indicator"
            data-connected={isConnected ? 'true' : 'false'}
            aria-label={status}
          >
            <span
              className={`h-2 w-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}
            />
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs">
          <div className="font-medium">{status}</div>
          {diagnostics && (
            <div className="mt-1 space-y-0.5 text-muted-foreground">
              {isConnected ? (
                <div>Uptime: {formatUptime(diagnostics.connectedSince)}</div>
              ) : (
                <div>Reconnect attempt: {diagnostics.reconnectAttempts}</div>
              )}
              <div>Disconnects: {diagnostics.disconnectCount}</div>
              {diagnostics.lastDisconnectReason && (
                <>
                  <div>Last reason: {diagnostics.lastDisconnectReason}</div>
                  <div>Last disconnect: {formatTimestamp(diagnostics.lastDisconnectTime)}</div>
                </>
              )}
            </div>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
